import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {RightSidebarFloatingPanel} from "../../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../../layout/rightSidebar/RightSidebarPanelHeader";
import {RightSidebarSearchButton} from "../../../layout/rightSidebar/RightSidebarRail";

export type ReservationRailSearchState =
    | "idle"
    | "loading"
    | "refreshing"
    | "error"
    | "empty"
    | "results";

interface ReservationRailSearchProps {
    query: string;
    onQueryChange: (query: string) => void;
    state: ReservationRailSearchState;
    children?: ReactNode | ((close: () => void) => ReactNode);
    onRetry?: () => void;
    idleMessage?: ReactNode;
    emptyMessage?: ReactNode;
    errorMessage?: ReactNode;
}

const StateMessage = ({children}: {children: ReactNode}) => (
    <Box sx={{display: "flex", justifyContent: "center", px: 3, py: 8, textAlign: "center"}}>
        <Typography variant="body2" color="text.secondary">
            {children}
        </Typography>
    </Box>
);

export function ReservationRailSearch({
    query,
    onQueryChange,
    state,
    children,
    onRetry,
    idleMessage,
    emptyMessage,
    errorMessage,
}: ReservationRailSearchProps) {
    const {t} = useTranslation();

    return (
        <RightSidebarSearchButton
            icon={(
                <Badge variant="dot" invisible={!query.trim()}>
                    <SearchOutlinedIcon/>
                </Badge>
            )}
            label={t("Search")}
            renderPanel={close => (
                <RightSidebarFloatingPanel width={380} sx={{overflow: "hidden"}}>
                    <Box sx={{display: {xs: "block", md: "none"}}}>
                        <RightSidebarPanelHeader title={t("Search")} onClose={close}/>
                    </Box>

                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{px: 2, pt: {xs: 1, md: 2}, pb: 2, flexShrink: 0}}
                    >
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            value={query}
                            placeholder={t("Search reservations")}
                            onChange={event => onQueryChange(event.target.value)}
                            onKeyDown={event => {
                                if (event.key === "Escape") {
                                    event.stopPropagation();
                                    close();
                                }
                            }}
                            inputProps={{"aria-label": t("Search")}}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlinedIcon fontSize="small"/>
                                    </InputAdornment>
                                ),
                                endAdornment: query ? (
                                    <InputAdornment position="end">
                                        <Tooltip title={t("Clear search")}>
                                            <IconButton
                                                type="button"
                                                size="small"
                                                aria-label={t("Clear search")}
                                                onMouseDown={event => event.preventDefault()}
                                                onClick={() => onQueryChange("")}
                                            >
                                                <CloseIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                    </Stack>

                    {state === "refreshing" && (
                        <LinearProgress aria-label={t("Refreshing")}/>
                    )}

                    <Box sx={{flex: 1, minHeight: 0, overflowY: "auto"}}>
                        {state === "idle" && (
                            <StateMessage>{idleMessage ?? t("Enter a search term")}</StateMessage>
                        )}
                        {state === "loading" && (
                            <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                                <CircularProgress size={24} aria-label={t("Loading")}/>
                            </Box>
                        )}
                        {state === "error" && (
                            <Stack alignItems="center" spacing={2} sx={{px: 3, py: 8, textAlign: "center"}}>
                                <Typography variant="body2" color="text.secondary">
                                    {errorMessage ?? t("Something went wrong")}
                                </Typography>
                                {onRetry && (
                                    <Button variant="outlined" size="small" onClick={onRetry}>
                                        {t("Try again")}
                                    </Button>
                                )}
                            </Stack>
                        )}
                        {state === "empty" && (
                            <StateMessage>{emptyMessage ?? t("No results found")}</StateMessage>
                        )}
                        {(state === "results" || state === "refreshing") && (
                            typeof children === "function" ? children(close) : children
                        )}
                    </Box>
                </RightSidebarFloatingPanel>
            )}
        />
    );
}
